import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listGroups, getGroupStatus, getGroupConfig, rerunGroup, runGroup } from './groupApi'

const ACTIVE_STATUSES = new Set(['queued', 'PENDING', 'RUNNING', 'running'])

export const isActiveGroupStatus = (status) => ACTIVE_STATUSES.has(String(status || ''))

export const useGroups = (workspaceID) => {
    const {
        data: groupsData,
        isLoading: loadingGroups,
        error: groupsError,
    } = useQuery({
        queryKey: ['domainIntelligenceGroups', workspaceID],
        queryFn: () => listGroups(workspaceID),
        enabled: Boolean(workspaceID),
        // Poll while any run is in flight so the operator can watch progress.
        refetchInterval: (query) => {
            const groups = query.state.data?.groups || []
            return groups.some((group) => isActiveGroupStatus(group.status)) ? 10000 : false
        },
    })

    return {
        groups: groupsData?.groups || [],
        loadingGroups,
        groupsError,
    }
}

export const useGroupStatus = (workspaceID, groupId) => {
    const {
        data: groupStatus,
        isLoading: loadingGroupStatus,
        error: groupStatusError,
    } = useQuery({
        queryKey: ['domainIntelligenceGroupStatus', workspaceID, groupId],
        queryFn: () => getGroupStatus(workspaceID, groupId),
        enabled: Boolean(workspaceID) && Boolean(groupId),
        refetchInterval: (query) =>
            isActiveGroupStatus(query.state.data?.status) ? 5000 : false,
    })

    return { groupStatus, loadingGroupStatus, groupStatusError }
}

export const useRerunGroup = (workspaceID) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (groupId) => rerunGroup(workspaceID, groupId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['domainIntelligenceGroups', workspaceID],
            })
        },
    })
}

export const useGroupConfig = (workspaceID, groupId) => {
    const {
        data: groupConfig,
        isLoading: loadingGroupConfig,
        error: groupConfigError,
    } = useQuery({
        queryKey: ['domainIntelligenceGroupConfig', workspaceID, groupId],
        queryFn: () => getGroupConfig(workspaceID, groupId),
        enabled: Boolean(workspaceID) && Boolean(groupId),
    })

    return { groupConfig, loadingGroupConfig, groupConfigError }
}

export const useRunGroup = (workspaceID) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (configJson) => runGroup(workspaceID, configJson),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['domainIntelligenceGroups', workspaceID],
            })
        },
    })
}
